import { memo } from "react";

interface BalloonProps {
  color: string;
  size: string;
  position: string;
  animationDelay?: string;
  opacity?: string;
}

const Balloon = memo(({ color, size, position, animationDelay = "0s", opacity = "1" }: BalloonProps) => (
  <div
    className={`absolute ${position} ${size} animate-float-slow`}
    style={{ animationDelay, opacity }}
  >
    <svg viewBox="0 0 100 140" className="w-full h-full drop-shadow-lg">
      {/* Balloon body */}
      <defs>
        <linearGradient id={`balloon-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.9" />
          <stop offset="50%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.7" />
        </linearGradient>
        <linearGradient id={`shine-${color}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="white" stopOpacity="0.4" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
      
      {/* Main balloon shape */}
      <ellipse cx="50" cy="45" rx="40" ry="45" fill={`url(#balloon-${color})`} />
      
      {/* Shine effect */}
      <ellipse cx="35" cy="35" rx="15" ry="20" fill={`url(#shine-${color})`} />
      
      {/* Balloon knot */}
      <polygon points="50,90 45,95 55,95" fill={color} />
      
      {/* Strings */}
      <path d="M 45 95 Q 40 110 50 120" stroke="#8B7355" strokeWidth="1.5" fill="none" />
      <path d="M 55 95 Q 60 110 50 120" stroke="#8B7355" strokeWidth="1.5" fill="none" />
      
      {/* Mini basket */}
      <rect x="40" y="120" width="20" height="12" rx="2" fill="#D4A574" />
      <rect x="40" y="120" width="20" height="3" rx="1" fill="#B8956E" />
    </svg>
  </div>
));

Balloon.displayName = "Balloon";

const BackgroundBalloons = () => {
  const balloons: BalloonProps[] = [
    { color: "#E8A0B7", size: "w-16 h-24", position: "top-20 left-4", animationDelay: "0s", opacity: "0.8" },
    { color: "#7EB5E8", size: "w-20 h-28", position: "top-12 right-8", animationDelay: "1s", opacity: "0.9" },
    { color: "#B5A0E8", size: "w-14 h-20", position: "top-40 right-2", animationDelay: "0.5s", opacity: "0.7" },
    { color: "#E8C0A0", size: "w-12 h-18", position: "bottom-60 left-2", animationDelay: "1.5s", opacity: "0.6" },
    { color: "#A0D8E8", size: "w-18 h-26", position: "bottom-52 right-6", animationDelay: "2s", opacity: "0.75" },
    { color: "#E8A0D8", size: "w-10 h-14", position: "top-32 left-8", animationDelay: "0.8s", opacity: "0.5" },
    { color: "#A0E8C0", size: "w-14 h-20", position: "bottom-80 left-6", animationDelay: "2.5s", opacity: "0.65" },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {balloons.map((balloon, index) => (
        <Balloon key={index} {...balloon} />
      ))}
      
      {/* Decorative clouds */}
      <div className="absolute top-16 left-1/4 w-32 h-12 bg-white/40 rounded-full blur-xl" />
      <div className="absolute top-28 right-1/4 w-40 h-16 bg-white/30 rounded-full blur-2xl" />
      <div className="absolute bottom-40 left-1/3 w-36 h-14 bg-white/35 rounded-full blur-xl" />
      <div className="absolute top-48 left-1/2 w-28 h-10 bg-white/25 rounded-full blur-2xl" />
    </div>
  );
};

export default memo(BackgroundBalloons);
